import React, { memo, useState } from "react";
import { ListGroup, Tooltip, OverlayTrigger } from "react-bootstrap";
import KaTeXText from "../KatexText";
import "./PreviewStatement.scss";
import parse from "html-react-parser";
import he from "he";

const renderStatement = (data, annotations = []) => {
  const textElements = [];
  let lastEnd = 0;

  // Sort annotations by start position
  const sortAnnotations = (annotations) => {
    if (!Array.isArray(annotations)) {
      console.warn("Annotations is not an array:", annotations);
      return [];
    }
    return annotations?.sort((a, b) => a?.start - b?.start);
  };

  const sortedAnnotations = sortAnnotations(annotations);

  // Iterate over annotations to add annotated text and plain text between annotations
  sortedAnnotations?.forEach((annotation) => {
    // Add text before the annotation
    if (lastEnd < annotation?.start) {
      textElements.push(
        <KaTeXText
          key={lastEnd}
          text={data?.statement?.substring(lastEnd, annotation?.start)}
        />
      );
    }

    // Annotated text with tooltip
    const tooltip = (
      <Tooltip id={`tooltip-${annotation.start}`}>{annotation?.note}</Tooltip>
    );
    textElements.push(
      <OverlayTrigger key={annotation.start} placement="top" overlay={tooltip}>
        <span style={{ backgroundColor: annotation.color }}>
          <KaTeXText
            text={data?.statement?.substring(
              annotation?.start,
              annotation?.end
            )}
          />
        </span>
      </OverlayTrigger>
    );

    lastEnd = annotation?.end;
  });

  // Add any remaining text after the last annotation
  if (lastEnd < data?.statement?.length) {
    textElements.push(
      <KaTeXText key={lastEnd} text={data?.statement?.substring(lastEnd)} />
    );
  }

  return (
    <ListGroup.Item style={{ backgroundColor: "transparent" }}>
      {textElements}
    </ListGroup.Item>
  );
};

const HighlightableHtml = ({
  htmlString,
  highlights,
  contentRef,
  handleMouseUp,
}) => {
  // This will update as we walk through text nodes
  let globalCharIndex = 0;
  const [hoveredIndex, setHoveredIndex] = useState(null);

  const renderWithHighlights = (node) => {
    if (typeof node === "string") {
      const parts = [];

      for (let i = 0; i < node.length; i++) {
        const currentIndex = globalCharIndex++;
        const highlight = highlights.find(
          (h) => currentIndex >= h.start && currentIndex < h.end
        );

        parts.push(
          <span
            key={currentIndex}
            style={{
              position: 'relative',
              backgroundColor: highlight?.color || 'transparent',
              cursor: highlight ? 'pointer' : 'text',
            }}
            onMouseEnter={() => highlight?.tooltip && setHoveredIndex(currentIndex)}
            onMouseLeave={() => setHoveredIndex(null)}
          >
            {node[i]}
            {highlight?.tooltip && hoveredIndex === currentIndex && (
              <span
                style={{
                  position: 'absolute',
                  bottom: '125%',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  backgroundColor: '#1E293B',
                  color: '#FFFFFF',
                  fontSize: '14px', // control size here
                  padding: '4px 8px',
                  borderRadius: '6px',
                  whiteSpace: 'nowrap',
                  zIndex: 10,
                  pointerEvents: 'none',
                }}
              >
                {highlight.tooltip}
              </span>
            )}
          </span>
        );
      }

      return parts;
    }

    if (React.isValidElement(node)) {
      const children = React.Children.map(
        node.props.children,
        renderWithHighlights
      );
      return React.cloneElement(node, { ...node.props, children });
    }

    return null;
  };

  const parsed = parse(htmlString);
  const wrappedParsed = Array.isArray(parsed) ? parsed : [parsed]; // Always ensure it's an array

  // Reset before each render
  globalCharIndex = 0;

  return (
    <div ref={contentRef} onMouseUp={handleMouseUp}>
      {wrappedParsed.map((node, idx) => (
        <React.Fragment key={idx}>{renderWithHighlights(node)}</React.Fragment>
      ))}
    </div>
  );
};

function PreviewStatement({
  data,
  contentRef,
  highlights,
  handleMouseUp,
  isEnglishModule = false,
}) {
  if (isEnglishModule) {
    const statement = he.decode(data?.statement);
    return (
      <HighlightableHtml
        htmlString={statement}
        contentRef={contentRef}
        highlights={highlights}
        handleMouseUp={handleMouseUp}
      />
    );
  }
  return (
    <div
      id="preview-statement"
      className="preview-statement-container"
      variant="flush"
    >
      {renderStatement(data)}
    </div>
  );
}

export default memo(PreviewStatement);
